import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Providers
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import { CreditGateProvider } from "@/components/CreditGateContext";
// iter878 — MethodologyProvider import removed (composite risk calculator deleted).

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Onboarding from "@/pages/Onboarding";
import ProgressiveProfile from "@/pages/ProgressiveProfile";
import UnsubscribePage from "@/pages/UnsubscribePage";
import PhenotypeSharePage from "@/pages/PhenotypeSharePage";
import AdminPanel from "@/pages/AdminPanel";
import AdminBhiRiskFactorsPage from "@/pages/AdminBhiRiskFactorsPage";  // iter980 — BHI Risk Factors admin upload
import AdminPatientsPage from "@/pages/AdminPatientsPage";  // iter1017 — dedicated patient roster
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";  // iter1018 — impersonation audit log
import AdminErrorInboxPage from "@/pages/AdminErrorInboxPage";  // iter1001 — вграден инбокс за грешки
import BhiBtrePage from "@/pages/BhiBtrePage";  // iter982 — BHI BTRE priorities
import LandingPage from "@/pages/LandingPage";   // iter389 — new landing
import LandingPageV2 from "@/pages/LandingPageV2";  // Прототип: Lean 7-section funnel за B2C
import ToolsHubPage from "@/pages/ToolsHubPage";  // Публичен hub на всички инструменти
import ForDoctorsPage from "@/pages/ForDoctorsPage";  // B2B портал за лекари и клиники
import WhyNutramiloPage from "@/pages/WhyNutramiloPage";  // iter671 — comparison + value page
import AboutPage from "@/pages/AboutPage";
import PlatformDescriptionPage from "@/pages/PlatformDescriptionPage";  // iter1068 — описание на платформата
import ConsultantsPage from "@/pages/ConsultantsPage";  // iter636 — Medical consultants page
import ConsultantDetailPage from "@/pages/ConsultantDetailPage";  // iter638 — One page per consultant
// iter593 — BreastHealthPage премахната по user request (had stale  copy on production)
import BreastHealthIntelligencePage from "@/pages/BreastHealthIntelligencePage";
import BreastHealthIntelligenceExportPage from "@/pages/BreastHealthIntelligenceExportPage";  // iter738 — print-friendly export
import BhiDashboardPreviewPage from "@/pages/BhiDashboardPreviewPage";  // iter1031 — two-column dashboard preview
import WearablesPage from "@/pages/WearablesPage";  // iter876 — Google Health OAuth
import TwinNetworkPage from "@/pages/TwinNetworkPage";  // iter683
import MaleHealthTwinPage from "@/pages/MaleHealthTwinPage";  // iter1030
import HematologyTwinPage from "@/pages/HematologyTwinPage";  // iter1032
import NeuroTwinPage from "@/pages/NeuroTwinPage";  // iter1035
import RespiratoryTwinPage from "@/pages/RespiratoryTwinPage";  // iter1051
import LabSheetPage from "@/pages/LabSheetPage";  // iter1053
import PhenotypeExplorerPage from "@/pages/PhenotypeExplorerPage";  // iter901
import InterventionSimulatorPage from "@/pages/InterventionSimulatorPage";  // iter903
import PathwayVaultPage from "@/pages/PathwayVaultPage";  // iter904
import CognitiveTwinPage from "@/pages/CognitiveTwinPage";  // iter600
import PhenotypeTwinPage from "@/pages/PhenotypeTwinPage";  // iter609
import MicrobiomeTwinPage from "@/pages/MicrobiomeTwinPage";  // iter609
import BodyCompositionTwinPage from "@/pages/BodyCompositionTwinPage";  // iter612
import SleepRecoveryTwinPage from "@/pages/SleepRecoveryTwinPage";  // iter612
import StressTwinPage from "@/pages/StressTwinPage";  // iter618
import ThyroidTwinPage from "@/pages/ThyroidTwinPage";  // iter621
import CardioTwinPage from "@/pages/CardioTwinPage";  // iter628
import HormonalTwinPage from "@/pages/HormonalTwinPage";  // iter628
import MetabolicInsulinTwinPage from "@/pages/MetabolicInsulinTwinPage";  // iter628
import InflammationTwinPage from "@/pages/InflammationTwinPage";  // iter629
import MovementTwinPage from "@/pages/MovementTwinPage";  // iter629
import MentalEmotionalTwinPage from "@/pages/MentalEmotionalTwinPage";  // iter629
import LiverKidneyTwinPage from "@/pages/LiverKidneyTwinPage";  // iter629
import KidneyHealthIntelligencePage from "@/pages/KidneyHealthIntelligencePage";
import MedicationsPage from "@/pages/MedicationsPage";  // KHI
import HepaticClinicalEnginePage from "@/pages/HepaticClinicalEnginePage";  // Etap 6 — Clinical Engine UI
import MuscularClinicalEnginePage from "@/pages/MuscularClinicalEnginePage";  // Etap 6 — Muscular freeze UI
import InflammatoryClinicalEnginePage from "@/pages/InflammatoryClinicalEnginePage";  // V5 — Inflammatory research preview (beta, not frozen)
import HereditaryRiskChecklist from "@/pages/HereditaryRiskChecklist";  // iter624
import LiveInsightToast from "@/components/LiveInsightToast";  // iter624
import LabTwinPage from "@/pages/LabTwinPage";  // iter601
import LabTwinNetworkPage from "@/pages/LabTwinNetworkPage";  // iter1113.c
import AdminReviewsPage from "@/pages/AdminReviewsPage";  // iter1114
import MyPromisePage from "@/pages/MyPromisePage";          // iter1117 — Обещание Nutramilo
import AdminPromisesPage from "@/pages/AdminPromisesPage";  // iter1117 — Админ преглед
import CreditsGuidePage from "@/pages/CreditsGuidePage";    // iter1118 — Какво струват AI действията
import DailyLimitDialog from "@/components/DailyLimitDialog"; // iter1118c — Модал при дневен лимит
import AdminGiftsAdoptionPage from "@/pages/AdminGiftsAdoptionPage"; // iter1118c
import MarkerMapPage from "@/pages/MarkerMapPage";  // iter1065
import TwinChatDock from "@/components/twin_chat/TwinChatDock";  // iter1066
// iter733 — Public standalone tools (moved off landing page)
import PhotoScannerPage from "@/pages/tools/PhotoScannerPage";
import CollagenPage from "@/pages/tools/CollagenPage";
import LabCoveragePage from "@/pages/LabCoveragePage";  // iter704 — public lab coverage
import DentalHealthIntelligencePage from "@/pages/DentalHealthIntelligencePage";
import SkinScreeningPage from "@/pages/SkinScreeningPage";
import SmartEatingFeaturePage from "@/pages/SmartEatingFeaturePage";
import RouteScrollReset from "@/components/RouteScrollReset";
import CompliancePage from "@/pages/CompliancePage";
import AppShell from "@/components/AppShell";
import DiaryPage from "@/pages/DiaryPage";
import AnalysesPage from "@/pages/AnalysesPage";
import HealthIntelligencePage from "@/pages/HealthIntelligencePage";
import LactationCoachPage from "@/pages/LactationCoachPage";  // iter460 — Lactation Coach module
import NutritionLabPage from "@/pages/NutritionLabPage";        // iter461 — Nutrition Intelligence Lab
import MindBodyLabPage from "@/pages/MindBodyLabPage";          // iter486 — Mind & Body Lab
import LipidTrackerPage from "@/pages/LipidTrackerPage";        // iter526 — Lipid (AIP) Tracker tab
import MyMenusPage from "@/pages/MyMenusPage";                   // iter466 — AI Menus history
import PlatePreviewPage from "@/pages/PlatePreviewPage";          // iter476 — Plate Simulator preview
// iter452: WomensHealthPage import removed — legacy route deleted
import WomensHealthV3Page from "@/pages/WomensHealthV3Page";
import SharedHealthIntelligencePage from "@/pages/SharedHealthIntelligencePage";
import DoctorInvitePage from "@/pages/DoctorInvitePage";  // iter567 — Doctor invite + approval landing
import SharedPatientReadOnlyPage from "@/pages/SharedPatientReadOnlyPage";  // iter570 — Phase 7c read-only doctor view
import RecipeVaultPage from "@/pages/RecipeVaultPage";
import DilAuditPage from "@/pages/DilAuditPage";
// iter315 — PlanPage премахнат, маршрутът редиректва към Анализи/Клиничен.
import ProfilePageFull from "@/pages/ProfilePageFull";
import PublicAISystemsPage from "@/pages/PublicAISystemsPage";
import DataTransfersPage from "@/pages/DataTransfersPage";
import NotFoundPage from "@/pages/NotFoundPage";  // iter1077
import ContactPage from "@/pages/ContactPage";
import PricingPage from "@/pages/PricingPage";
import ClinicianTrialApplyPage from "@/pages/ClinicianTrialApplyPage";
import HealthAssessmentPage from "@/pages/HealthAssessmentPage";
import ComparePage from "@/pages/ComparePage";
import LegalPage from "@/pages/LegalPage";
import PrivacyPage from "@/pages/PrivacyPage";  // iter881 — dedicated /privacy for Google OAuth
import TermsPage from "@/pages/TermsPage";      // iter881 — dedicated /terms for Google OAuth
import ChangelogPage from "@/pages/ChangelogPage";
import SymptomAssessment from "@/pages/SymptomAssessment";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MagicLink from "@/pages/MagicLink";
import AuthCallback from "@/components/AuthCallback";
import TrialExpiredPage from "@/pages/TrialExpiredPage";
import ClinicianPending from "@/pages/ClinicianPending";
import DoctorDashboardPage from "@/pages/DoctorDashboardPage";
import DoctorProfilePage from "@/pages/DoctorProfilePage";
import DoctorComposerPage from "@/pages/DoctorComposerPage";
import ActivatePatientPage from "@/pages/ActivatePatientPage";
// iter394 — BlogPage import removed (route /blog deprecated)
import FAQPage from "@/pages/FAQPage";
import RefundPage from "@/pages/RefundPage";
import ScienceLibrary from "@/pages/ScienceLibrary";
import PlanDetailPage from "@/pages/PlanDetailPage";
import GuidePage from "@/pages/GuidePage";
import TechnicalPage from "@/pages/TechnicalPage";
import ScientificFormulasPage from "@/pages/ScientificFormulasPage";
import AITransparencyPage from "@/pages/legal/AITransparencyPage";
import AIRiskSafetyPage from "@/pages/legal/AIRiskSafetyPage";
import ClinicalGovernancePage from "@/pages/legal/ClinicalGovernancePage";
import LimitationsPage from "@/pages/legal/LimitationsPage";
import InvestorsPage from "@/pages/InvestorsPage";
import AdvertisePage from "@/pages/AdvertisePage";
import AnonymousLabUploadPage from "@/pages/AnonymousLabUploadPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";
// iter1073 — навигаторът е скрит по заявка; вносът е изключен, за да не се
// оплаква сглобяването за неползван компонент.
// import CoachAINutramilo from "@/components/CoachAINutramilo";
import TawkToWidget from "@/components/TawkToWidget";  // iter640 — Tawk.to live chat
import ImpersonationBanner from "@/components/ImpersonationBanner";  // iter641 — Admin „Login as user" banner
import BugReportButton from "@/components/BugReportButton";  // iter648 — User „Report broken component" widget
import InstallAppPrompt from "@/components/InstallAppPrompt";  // iter1078
import GA4Tracker from "@/components/GA4Tracker";
import { DashboardLayoutProvider } from "@/dashboard/DashboardLayoutContext";  // iter553 — global so all pages can hide/show widgets
import { DashboardModeProvider } from "@/dashboard/DashboardModeContext";  // iter561 — Easy/Standard/Expert disclosure
import ErrorBoundary from "@/components/ErrorBoundary";  // iter556 — catch render errors so users never see a blank white page
import { installErrorReporter } from "@/lib/errorReporter";  // iter1001 — вграден инбокс за грешки

installErrorReporter();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cookie-first auth (iter264, hardened iter312): backend sets HttpOnly
// access_token + refresh_token cookies on login/register/refresh/magic-link
// AND returns them in the response body. Frontend keeps the body tokens in
// sessionStorage + axios Authorization header as a defensive fallback for
// browsers/contexts where the cookies are blocked (cross-site deploy,
// third-party cookie blocking, Safari ITP). withCredentials still ensures
// cookies are attached when available.
axios.defaults.withCredentials = true;

// Token storage keys — sessionStorage (cleared on tab close, no XSS persistence)
const TOKEN_KEY = "nutramilo_access_token";
const REFRESH_KEY = "nutramilo_refresh_token";

const saveTokens = (accessToken, refreshToken) => {
  // iter312: actually persist tokens as a fallback when HttpOnly cookies
  // can't be relied on (cross-site deploys, third-party cookie blocking).
  // sessionStorage is preferred over localStorage to limit XSS exposure.
  try {
    if (accessToken) {
      sessionStorage.setItem(TOKEN_KEY, accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }
    if (refreshToken) {
      sessionStorage.setItem(REFRESH_KEY, refreshToken);
    }
  } catch { /* storage disabled — cookies will carry auth */ }
};

const getAccessToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const getRefreshToken = () => {
  try { return sessionStorage.getItem(REFRESH_KEY); } catch { return null; }
};

const clearTokens = () => {
  // Purge tokens from every known location
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem("access_token");   // legacy MagicLink key
    localStorage.removeItem("refresh_token");  // legacy MagicLink key
  } catch { /* storage disabled — ignore */ }
  delete axios.defaults.headers.common.Authorization;
};

// Bootstrap Authorization header from sessionStorage on app load so reloads
// (within the same tab) don't lose auth even if cookies got blocked.
try {
  const _bootToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  if (_bootToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${_bootToken}`;
  }
} catch { /* ignore */ }

// Request interceptor — ensures Authorization header is set when we have a
// session token (defensive against axios.defaults being cleared).
axios.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    const tok = getAccessToken()
      || (typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null);
    if (tok) {
      config.headers.Authorization = `Bearer ${tok}`;
    }
  }
  return config;
});

// Auto-refresh interceptor: retry on 401 by refreshing the access token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // iter1118c — 429 с ясно съобщение за дневен лимит от „Подарък" →
    // излъчваме глобално събитие, което `DailyLimitDialog` слуша и
    // показва приятелски модал с бутон „Напомни ми утре".
    try {
      const detail = error?.response?.data?.detail || "";
      if (
        error?.response?.status === 429 &&
        typeof detail === "string" &&
        detail.includes("дневния лимит")
      ) {
        window.dispatchEvent(new CustomEvent("nutramilo:daily-limit-hit"));
      }
    } catch { /* ignore */ }

    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/register")
    ) {
      // iter1019 — Do NOT auto-refresh during impersonation. The refresh
      // cookie/token still belongs to the admin (or doctor) — silently
      // refreshing would swap the session BACK to the original actor and
      // the UI would silently show the wrong account. Instead, force a
      // clean exit from impersonation and let the user log back in.
      let impersonating = false;
      try { impersonating = localStorage.getItem("impersonation_active") === "true"; } catch { /* ignore */ }
      if (impersonating) {
        try {
          const adminToken = localStorage.getItem("impersonation_admin_token") || "";
          const adminRefresh = localStorage.getItem("impersonation_admin_refresh") || "";
          const returnPath = localStorage.getItem("impersonation_return_path") || "/admin";
          localStorage.removeItem("impersonation_active");
          localStorage.removeItem("impersonation_target_email");
          localStorage.removeItem("impersonation_readonly");
          localStorage.removeItem("impersonation_admin_token");
          localStorage.removeItem("impersonation_admin_refresh");
          localStorage.removeItem("impersonation_return_path");
          if (adminToken) sessionStorage.setItem(TOKEN_KEY, adminToken);
          if (adminRefresh) sessionStorage.setItem(REFRESH_KEY, adminRefresh);
          if (adminToken) axios.defaults.headers.common.Authorization = `Bearer ${adminToken}`;
          window.location.href = returnPath;
        } catch { /* ignore */ }
        return Promise.reject(error);
      }
      if (isRefreshing) {
        // Wait for the in-flight refresh, then retry with the NEW header.
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // iter910 — force header refresh; interceptor will re-add current tok
          if (originalRequest.headers) delete originalRequest.headers.Authorization;
          return axios(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        // iter910 — forward the refresh token from sessionStorage so refresh
        // works in cross-domain preview environments where cookies may not
        // travel (SameSite=lax + third-party). Also SAVE the new tokens so
        // subsequent requests use the fresh Bearer.
        const stored = getRefreshToken();
        const refreshBody   = stored ? { refresh_token: stored } : {};
        const refreshConfig = stored ? { headers: { "X-Refresh-Token": stored } } : {};
        const { data } = await axios.post(`${API}/auth/refresh`, refreshBody, refreshConfig);
        if (data?.access_token) {
          saveTokens(data.access_token, data.refresh_token);
        }
        processQueue(null);
        // Force the retried request to pick up the fresh header.
        if (originalRequest.headers) delete originalRequest.headers.Authorization;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Format API error helper
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Auth Provider Component
const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const silentRefresh = useCallback(async () => {
    // iter1019 — Never silently refresh during impersonation. Admin's
    // refresh_token in sessionStorage (or the HttpOnly cookie) would
    // swap the session back to the admin and the impersonation UI would
    // show the WRONG user without any visible cue.
    try {
      if (localStorage.getItem("impersonation_active") === "true") return;
    } catch { /* ignore */ }
    try {
      // Refresh token from cookie OR sessionStorage fallback (iter312).
      const stored = getRefreshToken();
      const refreshBody = stored ? { refresh_token: stored } : {};
      const refreshConfig = stored ? { headers: { "X-Refresh-Token": stored } } : {};
      const { data } = await axios.post(`${API}/auth/refresh`, refreshBody, refreshConfig);
      if (data?.access_token) {
        saveTokens(data.access_token, data.refresh_token);
      }
    } catch (e) {
      // refresh token expired — session truly over
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // Cookie-based auth: call /auth/me directly. Backend returns 401 if no
      // valid cookie; the response interceptor will attempt a silent refresh.
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      // Interceptor already tried refresh — session is truly over.
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Proactive silent refresh every 50 min to keep session alive
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(silentRefresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, silentRefresh]);

  // Refresh when tab regains focus (user comes back after a while)
  useEffect(() => {
    if (!user) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        silentRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [user, silentRefresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // 2FA — caller must handle the challenge flow
    if (data?.two_factor_required) {
      return { twoFactorRequired: true, challengeId: data.challenge_id, expiresIn: data.expires_in };
    }
    saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const verifyTwoFactor = async (challengeId, code) => {
    const { data } = await axios.post(`${API}/auth/verify-2fa`, { challenge_id: challengeId, code });
    saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, referralCode, consents = {}) => {
    // iter304 — GDPR Art. 9 requires explicit health-data consent at signup.
    const payload = {
      email, password, name,
      health_data_consent: !!consents.health_data_consent,
      age_confirmed_16_plus: !!consents.age_confirmed_16_plus,
      terms_version_accepted: consents.terms_version_accepted || "v1.0-2026-02",
      privacy_version_accepted: consents.privacy_version_accepted || "v1.0-2026-02",
    };
    // iter309 — optional DOB for stronger GDPR Art. 8 audit trail
    if (consents.date_of_birth) payload.date_of_birth = consents.date_of_birth;
    if (referralCode) payload.referral_code = referralCode;
    // iter1118 — optional patient-gift promo code
    if (consents.promo_code) payload.promo_code = consents.promo_code;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    if (data.status === "verification_required") {
      // Don't set user yet — need verification
      return data;
    }
    saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const verifyEmail = async (email, code) => {
    const { data } = await axios.post(`${API}/auth/verify-email`, { email, code });
    saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      // Fire both — one for JWT sessions, one for Emergent Google sessions.
      // Whichever cookie is set gets cleared server-side.
      await Promise.allSettled([
        axios.post(`${API}/auth/logout`),
        axios.post(`${API}/auth/google/logout`, {}, { withCredentials: true }),
      ]);
    } catch (e) {
      // Continue anyway
    }
    clearTokens();
    setUser(false);
  };

  // Used by Google Auth callback — set user from data without API call
  const loginWithData = (userData) => {
    saveTokens(userData.access_token, userData.refresh_token);
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      try {
        // Cookie-based refresh, with body+header fallback (iter312).
        const stored = getRefreshToken();
        const refreshBody = stored ? { refresh_token: stored } : {};
        const refreshConfig = stored ? { headers: { "X-Refresh-Token": stored } } : {};
        const refreshed = await axios.post(`${API}/auth/refresh`, refreshBody, refreshConfig);
        if (refreshed?.data?.access_token) {
          saveTokens(refreshed.data.access_token, refreshed.data.refresh_token);
        }
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch (e2) {
        clearTokens();
        setUser(false);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyTwoFactor, register, verifyEmail, logout, refreshUser, loginWithData, API }}>
      {children}
    </AuthContext.Provider>
  );
};


// iter460 — Legacy redirect for the un-prefixed /analyses URL. Preserves the
// search string and hash (e.g. ?tab=clinical#vais) so deep-links from the
// VAIS teaser, marketing emails, and shared snippets keep working.
const LegacyAnalysesRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/dashboard/analyses${location.search}${location.hash}`}
      replace
    />
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowExpired = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-4 border-[#0EA5E9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Skip trial check for admins, free users, and allowExpired routes
  const isAdmin = user.role === "admin";
  const isClinician = user.role === "clinician";
  const clinicianStatus = user.clinician_status;
  const subStatus = user.subscription?.status;
  const isFree = subStatus === "free";
  const isExpired = subStatus === "expired" || subStatus === "trial_expired";
  // Only the real 6-step Health Assessment (/onboarding) flips the flag.
  // latest_assessment is kept separately and does NOT imply completion.
  const hasProfile = user.profile?.completedOnboarding === true;

  // Pending/rejected clinicians → redirect to pending page
  if (isClinician && clinicianStatus !== "approved" && location.pathname !== "/clinician-pending") {
    return <Navigate to="/clinician-pending" replace />;
  }

  // Allowlist: routes a new (un-onboarded) user is allowed to visit without
  // being pushed to /onboarding. /dashboard is allowed because we use panel-
  // level locks to keep meal-logging accessible while prompting for the
  // Health Assessment.
  const ONBOARDING_ALLOWLIST = ["/onboarding", "/assessment", "/dashboard", "/unsubscribe"];

  if (!isAdmin && !isClinician && !hasProfile && !ONBOARDING_ALLOWLIST.some(p => location.pathname === p || location.pathname.startsWith(p + "/"))) {
    return <Navigate to="/onboarding" replace />;
  }

  // Free users can access the dashboard (with limited features)
  if (!isAdmin && !allowExpired && !isFree && isExpired && location.pathname !== "/upgrade") {
    return <Navigate to="/upgrade" replace />;
  }

  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-4 border-[#0EA5E9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // Pending clinicians → pending page
    if (user.role === "clinician" && user.clinician_status !== "approved") {
      return <Navigate to="/clinician-pending" replace />;
    }
    // New users without completed Health Assessment → onboarding first
    const hasProfile = user.profile?.completedOnboarding === true;
    if (!hasProfile && user.role !== "clinician") {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // Hooks MUST be called before any early return (rules-of-hooks)
  const location = useLocation();

  // iter395 — Capture ?ref=DOCTOR_SLUG once on first mount and any URL change.
  // Stored in localStorage with 90-day TTL, used later at signup/checkout for
  // commission attribution.
  useEffect(() => {
    if (location.search && location.search.includes("ref=")) {
      // Lazy import to keep first paint snappy
      import("@/lib/referral").then((m) => m.captureReferralFromURL());
    }
  }, [location.search]);

  // ── GA4 SPA route tracking — gtag е зареден в index.html. Тук изпращаме
  //    page_view при всяка промяна на URL, тъй като React Router не трига full reload. ──
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    } catch { /* silent — analytics не трябва да чупи app-а */ }
  }, [location.pathname, location.search]);

  // CRITICAL: Intercept Google OAuth callback (session_id in hash) before any routing
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Feb 2026 — Emergent Google Auth callback detector.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  // Detect the fragment SYNCHRONOUSLY during render so we never race the
  // AuthProvider's /auth/me check. Renders ONLY the callback view.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
    <Routes>
      <Route path="/" element={<LandingPageV2 />} />
      <Route path="/landing-v2" element={<Navigate to="/" replace />} />
      <Route path="/landing-legacy" element={<LandingPage />} />  {/* архив на стария дизайн */}
      <Route path="/info" element={<Navigate to="/" replace />} />
      <Route path="/info/:slug" element={<Navigate to="/" replace />} />
      <Route path="/assessment" element={<SymptomAssessment />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/share/phenotype/:token" element={<PhenotypeSharePage />} />
      <Route path="/share/health/:token" element={<SharedHealthIntelligencePage />} />
      <Route path="/share/doctor/:token" element={<DoctorInvitePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/platform" element={<PlatformDescriptionPage />} />
      <Route path="/labs" element={<LabCoveragePage />} />
      {/* iter733 — Public standalone tools */}
      <Route path="/tools" element={<ToolsHubPage />} />
      <Route path="/for-doctors" element={<ForDoctorsPage />} />
      <Route path="/tools/scanner" element={<PhotoScannerPage />} />
      <Route path="/tools/collagen" element={<CollagenPage />} />
      <Route path="/consultants" element={<ConsultantsPage />} />
      <Route path="/consultants/:slug" element={<ConsultantDetailPage />} />
      {/* iter593 — /breast-health премахната; redirect към BHI (изисква login) или landing */}
      <Route path="/breast-health" element={<Navigate to="/health-intelligence/breast" replace />} />
      <Route path="/health-intelligence/breast" element={<BreastHealthIntelligencePage />} />
      <Route path="/health-intelligence/breast/export" element={<BreastHealthIntelligenceExportPage />} />
      <Route path="/health-intelligence/breast/dashboard-preview" element={<BhiDashboardPreviewPage />} />
      <Route path="/settings/wearables" element={<ProtectedRoute><WearablesPage /></ProtectedRoute>} />  {/* iter876 */}
      <Route path="/health-intelligence/dental" element={<DentalHealthIntelligencePage />} />
      <Route path="/twins/network" element={<ProtectedRoute><TwinNetworkPage /></ProtectedRoute>} />  {/* iter683 */}
      <Route path="/dashboard/male-health-twin" element={<ProtectedRoute><MaleHealthTwinPage /></ProtectedRoute>} />  {/* iter1030 */}
      <Route path="/dashboard/hematology-twin" element={<ProtectedRoute><HematologyTwinPage /></ProtectedRoute>} />  {/* iter1032 */}
      <Route path="/dashboard/neuro-twin" element={<ProtectedRoute><NeuroTwinPage /></ProtectedRoute>} />  {/* iter1035 */}
      <Route path="/dashboard/respiratory-twin" element={<ProtectedRoute><RespiratoryTwinPage /></ProtectedRoute>} />  {/* iter1051 */}
      <Route path="/dashboard/lab-sheet" element={<ProtectedRoute><LabSheetPage /></ProtectedRoute>} />  {/* iter1053 */}
      <Route path="/phenotype-explorer" element={<ProtectedRoute><PhenotypeExplorerPage /></ProtectedRoute>} />  {/* iter901 */}
      <Route path="/intervention-simulator" element={<ProtectedRoute><InterventionSimulatorPage /></ProtectedRoute>} />  {/* iter903 */}
      <Route path="/pathway-vault" element={<ProtectedRoute><PathwayVaultPage /></ProtectedRoute>} />  {/* iter904 */}
      {/* iter460 — legacy redirect: /analyses → /dashboard/analyses (preserves
          query + hash like ?tab=clinical#vais). Some marketing emails / cards
          still point to the un-prefixed path. */}
      <Route path="/analyses" element={<LegacyAnalysesRedirect />} />
      {/* iter394 — /blog removed per user request */}
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/clinician-trial-apply" element={<ClinicianTrialApplyPage />} />
      <Route path="/health-assessment" element={<HealthAssessmentPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />  {/* iter881 — dedicated URL for Google OAuth consent */}
      <Route path="/terms" element={<TermsPage />} />      {/* iter881 — dedicated URL for Google OAuth consent */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/refund" element={<RefundPage />} />
      {/* Микросайтът „Наука" е премахнат — съдържанието се пренася към отделна платформа.
          Всички стари адреси се пренасочват към началната страница. */}
      <Route path="/science" element={<Navigate to="/" replace />} />
      <Route path="/science/*" element={<Navigate to="/" replace />} />
      <Route path="/knowledge-library" element={<ScienceLibrary />} />
      <Route path="/plan/:planId" element={<PlanDetailPage />} />
      <Route path="/guide" element={<GuidePage />} />
      {/* Партньорската програма е премахната от публичния сайт. */}
      <Route path="/ambassadors" element={<Navigate to="/" replace />} />
      <Route path="/ambassadors/*" element={<Navigate to="/" replace />} />
      <Route path="/technical" element={<TechnicalPage />} />
      <Route path="/scientific-formulas" element={<ScientificFormulasPage />} />
      <Route path="/legal/ai-transparency" element={<AITransparencyPage />} />
      <Route path="/legal/ai-risk-safety" element={<AIRiskSafetyPage />} />
      <Route path="/legal/clinical-governance" element={<ClinicalGovernancePage />} />
      <Route path="/legal/limitations" element={<LimitationsPage />} />
      <Route path="/investors" element={<InvestorsPage />} />
      <Route path="/advertise" element={<AdvertisePage />} />
      <Route path="/why-nutramilo" element={<WhyNutramiloPage />} />
      <Route path="/anonymous-lab" element={<AnonymousLabUploadPage />} />
      {/* <Route path="/changelog" element={<ChangelogPage />} /> */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/magic-link" element={<MagicLink />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dental-screening" element={<Navigate to="/tools" replace />} />
      <Route path="/dental-screening/xray" element={<Navigate to="/tools" replace />} />
      <Route path="/skin-screening" element={<SkinScreeningPage />} />
      <Route path="/smart/:slug" element={<SmartEatingFeaturePage />} />
      <Route path="/compliance" element={<CompliancePage />} />
      {/* iter600 — Cognitive Twin */}
      <Route path="/dashboard/cognitive-twin" element={<ProtectedRoute><CognitiveTwinPage /></ProtectedRoute>} />
      {/* iter601 — Lab Twin Simulator */}
      <Route path="/dashboard/lab-twin" element={<ProtectedRoute><LabTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/lab-twin-network" element={<ProtectedRoute><LabTwinNetworkPage /></ProtectedRoute>} />
      <Route path="/admin/reviews" element={<ProtectedRoute><AdminReviewsPage /></ProtectedRoute>} />
      <Route path="/my-promise" element={<ProtectedRoute><MyPromisePage /></ProtectedRoute>} />
      <Route path="/admin/promises" element={<ProtectedRoute><AdminPromisesPage /></ProtectedRoute>} />
      <Route path="/admin/gifts-adoption" element={<ProtectedRoute><AdminGiftsAdoptionPage /></ProtectedRoute>} />
      <Route path="/credits-guide" element={<CreditsGuidePage />} />
      <Route path="/dashboard/marker-map" element={<ProtectedRoute><MarkerMapPage /></ProtectedRoute>} />
      {/* iter609 — Phenotype Discovery + Gut Microbiome twins */}
      <Route path="/dashboard/phenotype-twin" element={<ProtectedRoute><PhenotypeTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/microbiome-twin" element={<ProtectedRoute><MicrobiomeTwinPage /></ProtectedRoute>} />
      {/* iter612 — Body Composition + Sleep & Recovery twins */}
      <Route path="/dashboard/body-comp-twin" element={<ProtectedRoute><BodyCompositionTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/sleep-twin" element={<ProtectedRoute><SleepRecoveryTwinPage /></ProtectedRoute>} />
      {/* iter618 — Stress & Cortisol Twin */}
      <Route path="/dashboard/stress-twin" element={<ProtectedRoute><StressTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/thyroid-twin" element={<ProtectedRoute><ThyroidTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/cardio-twin" element={<ProtectedRoute><CardioTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/hormonal-twin" element={<ProtectedRoute><HormonalTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/metabolic-insulin-twin" element={<ProtectedRoute><MetabolicInsulinTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/inflammation-twin" element={<ProtectedRoute><InflammationTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/movement-twin" element={<ProtectedRoute><MovementTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/mental-emotional-twin" element={<ProtectedRoute><MentalEmotionalTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/liver-kidney-twin" element={<ProtectedRoute><LiverKidneyTwinPage /></ProtectedRoute>} />
      <Route path="/dashboard/kidney-intelligence" element={<ProtectedRoute><KidneyHealthIntelligencePage /></ProtectedRoute>} />
      <Route path="/dashboard/medications" element={<ProtectedRoute><MedicationsPage /></ProtectedRoute>} />
      <Route path="/dashboard/clinical-engine/hepatic" element={<ProtectedRoute><HepaticClinicalEnginePage /></ProtectedRoute>} />
      <Route path="/dashboard/clinical-engine/muscular" element={<ProtectedRoute><MuscularClinicalEnginePage /></ProtectedRoute>} />
      <Route path="/dashboard/clinical-engine/inflammatory" element={<ProtectedRoute><InflammatoryClinicalEnginePage /></ProtectedRoute>} />
      <Route path="/dashboard/hereditary-risk" element={<ProtectedRoute><HereditaryRiskChecklist /></ProtectedRoute>} />
      {/* iter595 — legacy /recipe-vault link aliased to canonical /dashboard/recipes */}
      <Route path="/recipe-vault" element={<Navigate to="/dashboard/recipes" replace />} />
      <Route path="/profile/progressive" element={<ProtectedRoute><ProgressiveProfile /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="diary" element={<DiaryPage />} />
        <Route path="recipes" element={<RecipeVaultPage />} />
        <Route path="dil-audit" element={<DilAuditPage />} />
        <Route path="analyses" element={<AnalysesPage />} />
        <Route path="health-intelligence" element={<HealthIntelligencePage />} />
        {/* iter460 — Lactation Coach (self-identification only — not in nav by default) */}
        <Route path="lactation" element={<LactationCoachPage />} />
        {/* iter461 — Nutrition Intelligence Lab (Pro+ premium module) */}
        <Route path="nutrition-lab" element={<NutritionLabPage />} />
        {/* iter486 — Mind & Body Lab (Lifestyle Medicine Engine) */}
        <Route path="mindbody-lab" element={<MindBodyLabPage />} />
        {/* iter522 — Cortisol Tracker tab (Health Hub) */}
        <Route path="cortisol-tracker" element={<Navigate to="/dashboard/stress-twin" replace />} />
        {/* iter526 — Lipid (AIP) Tracker tab */}
        <Route path="lipid-tracker" element={<LipidTrackerPage />} />
        {/* iter466 — My AI Menus history */}
        <Route path="my-menus" element={<MyMenusPage />} />
        {/* iter476 — Plate Simulator preview (redirects to diary's plate sim in iter479) */}
        <Route path="plate-simulator" element={<Navigate to="/dashboard/diary?focus=plate-simulator#plate-simulator-anchor" replace />} />
        <Route path="womens-health" element={<WomensHealthV3Page />} />
        {/* legacy WomensHealthPage removed iter452 — superseded by v3 architecture */}
        <Route path="plan" element={<Navigate to="/dashboard/analyses?tab=clinical" replace />} />
        <Route path="profile" element={<ProfilePageFull />} />
      </Route>
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/patients" element={<ProtectedRoute><AdminPatientsPage /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute><AdminAuditLogPage /></ProtectedRoute>} />
      <Route path="/admin/errors" element={<ProtectedRoute><AdminErrorInboxPage /></ProtectedRoute>} />
      <Route path="/dashboard/admin/bhi-risk-factors" element={<ProtectedRoute><AdminBhiRiskFactorsPage /></ProtectedRoute>} />
      <Route path="/admin/bhi-risk-factors" element={<ProtectedRoute><AdminBhiRiskFactorsPage /></ProtectedRoute>} />
      <Route path="/dashboard/bhi/btre" element={<ProtectedRoute><BhiBtrePage /></ProtectedRoute>} />
      <Route path="/doctor" element={<ProtectedRoute><DoctorDashboardPage /></ProtectedRoute>} />
      <Route path="/doctor/profile" element={<ProtectedRoute><DoctorProfilePage /></ProtectedRoute>} />
      <Route path="/doctor/composer" element={<ProtectedRoute><DoctorComposerPage /></ProtectedRoute>} />
      <Route path="/doctor/composer/:patientId" element={<ProtectedRoute><DoctorComposerPage /></ProtectedRoute>} />
      <Route path="/doctor/shared/:patientId" element={<ProtectedRoute><SharedPatientReadOnlyPage /></ProtectedRoute>} />
      <Route path="/activate/:token" element={<ActivatePatientPage />} />
      <Route path="/upgrade" element={<ProtectedRoute allowExpired={true}><TrialExpiredPage /></ProtectedRoute>} />
      <Route path="/clinician-pending" element={<ProtectedRoute allowExpired={true}><ClinicianPending /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute allowExpired={true}><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/return"  element={<ProtectedRoute allowExpired={true}><PaymentSuccess /></ProtectedRoute>} />
      {/* iter368 — Public AI Systems Register (no auth, EU AI Act Art. 49) */}
      <Route path="/privacy/ai-systems" element={<PublicAISystemsPage />} />
      {/* iter369 P2 GAP-11 — SCCs / Data transfers disclosure */}
      <Route path="/legal/data-transfers" element={<DataTransfersPage />} />
      {/* iter1077 — кратки адреси и край на празния екран при непознат адрес */}
      <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
      <Route path="/diary" element={<Navigate to="/dashboard/diary" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    {/* iter1066 — разговор с мрежата / с отделния близнак (сгъваем, долу вдясно) */}
    <TwinChatDock />
    {/* iter1073 — навигаторът „Nutramilo AI" е скрит по заявка на потребителя
        (разговорът с мрежата на близнаците го замества). Компонентът остава в
        кода и се връща с разкоментиране. */}
    {/* <CoachAINutramilo /> */}
    {/* iter640 — Tawk.to live chat (bottom-right) */}
    <TawkToWidget />
    {/* iter641 — Persistent banner shown while admin is impersonating a user */}
    <ImpersonationBanner />
    {/* iter648 — Floating „Report broken component" widget for authed users */}
    <BugReportButton />
    {/* iter1078 — „Добавете на началния екран" (Android бутон / iPhone стъпки) */}
    <InstallAppPrompt />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <CreditGateProvider>
            <DashboardLayoutProvider>
            <DashboardModeProvider>
            <div className="App">
              <GA4Tracker />
              <RouteScrollReset />
              <ErrorBoundary label="App">
                <AppRoutes />
              </ErrorBoundary>
              <CookieConsentBanner />
              <Toaster position="top-right" richColors />
              <DailyLimitDialog />
              <LiveInsightToast />
            </div>
            </DashboardModeProvider>
            </DashboardLayoutProvider>
            </CreditGateProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
